/**
 * `/login` — staff sign-in.
 *
 * Email + password, validated against `adminLoginRequestSchema` — the same
 * contract the API validates the body with. On success the API sets the
 * httpOnly refresh cookie and this app holds nothing but an in-memory access
 * token and the staff name.
 *
 * The failure message is whatever the API chose: it is deliberately the same
 * sentence for an unknown address and a wrong password, so this screen must not
 * try to be more specific than that.
 */
import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { adminLoginRequestSchema } from '@wend/shared';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, FormField, Input } from '@wend/ui';

import { useAdminAuth } from '../auth/AuthProvider';
import { FormAlert } from '../components/FormAlert';
import { describeApiError, toApiError, type ApiError } from '../lib/api-error';
import { NO_FIELD_ERRORS, zodFieldErrors, type FieldErrorMap } from '../lib/form';

/**
 * Only ever return to a path on this origin. `//host` is a protocol-relative
 * URL, so it is rejected too.
 */
function redirectTarget(state: unknown): string {
  if (typeof state === 'object' && state !== null && 'from' in state) {
    const from = (state as { from?: unknown }).from;
    if (typeof from === 'string' && from.startsWith('/') && !from.startsWith('//')) {
      return from;
    }
  }
  return '/tenants';
}

export function LoginPage() {
  const { admin, signIn } = useAdminAuth();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FieldErrorMap>(NO_FIELD_ERRORS);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const target = redirectTarget(location.state);

  if (admin !== null) {
    return <Navigate to={target} replace />;
  }

  const submit = async (): Promise<void> => {
    setFailure(null);

    const parsed = adminLoginRequestSchema.safeParse({ email, password });
    if (!parsed.success) {
      setErrors(zodFieldErrors(parsed.error));
      return;
    }

    setErrors(NO_FIELD_ERRORS);
    setSubmitting(true);

    try {
      await signIn(parsed.data);
      // Nothing to navigate to: the session lands in context and the redirect
      // above takes over on the next render.
    } catch (error: unknown) {
      setFailure(toApiError(error));
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void submit();
  };

  return (
    <div className="relative min-h-dvh bg-ink text-text">
      <div className="wend-ambient" aria-hidden="true" />

      <main className="relative z-10 mx-auto flex min-h-dvh w-full max-w-[26rem] flex-col justify-center px-pad py-12">
        <Card>
          <CardHeader>
            <p className="font-mono text-[0.68rem] uppercase tracking-[0.16em] text-brand-b">
              Wend · internal
            </p>
            <CardTitle>Admin sign in</CardTitle>
            <CardDescription>
              Staff accounts only. Workspace owners sign in to the Wend app instead.
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
              {failure !== null && (
                <FormAlert requestId={failure.requestId}>{describeApiError(failure)}</FormAlert>
              )}

              <FormField label="Email" error={errors.email} required>
                <Input
                  type="email"
                  name="email"
                  autoComplete="username"
                  autoFocus
                  spellCheck={false}
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                  }}
                />
              </FormField>

              <FormField label="Password" error={errors.password} required>
                <Input
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                  }}
                />
              </FormField>

              <Button type="submit" loading={submitting} fullWidth>
                Sign in
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
